/**
 * Multi-format content specs + prompt builder for the Content Engine (Ask #6).
 *
 * WHY: from one brief/topic the engine should produce SEVERAL platform
 * formats at once — a LinkedIn narrative post, an X one-liner, an X thread
 * opener, and a short email — each generated by the real Claude worker
 * (when `VITE_AGENT_API_URL` is configured) and auto-graded by the shipped
 * scorer in `lib/crm/contentScore` (never a reimplementation).
 *
 * PURE by contract: no React, no network, no store, no clock. Given a topic
 * and a format spec, `buildGenerationPrompt` returns the exact prompt the
 * worker receives — including the format label, target platform, and every
 * constraint — so tests can pin the wire content and the view stays thin.
 *
 * The constraints intentionally mirror the scorer's best-practice axes
 * (hook, specificity, soft-sell placement, platform length bands) so drafts
 * that follow the prompt also grade well — one source of truth, two uses.
 */

import type { Platform } from '../crm/contentScore';

/** One platform format the engine can generate from a single brief. */
export interface ContentFormatSpec {
  /** Stable id — also the render key and the worker persona hint. */
  id: 'linkedin-post' | 'x-one-liner' | 'x-thread-opener' | 'short-email';
  /** Human label shown on the result card. */
  label: string;
  /** Where this piece is meant to be published, human-readable. */
  platform: string;
  /** One-line description for the card subtitle. */
  description: string;
  /** Platform constraints embedded verbatim in the generation prompt. */
  constraints: string[];
  /** Length norms the shipped scorer grades against (do NOT reimplement scoring). */
  scorePlatform: Platform;
}

/**
 * The four format specs, in render order. Returned as a fresh array each
 * call so callers can't mutate a shared module singleton.
 */
export function contentFormats(): ContentFormatSpec[] {
  return [
    {
      id: 'linkedin-post',
      label: 'LinkedIn narrative post',
      platform: 'LinkedIn',
      description: 'Story-first narrative with a soft ask at the end.',
      constraints: [
        'Write 80-300 words as a first-person narrative',
        'Open on a specific person, scene, or number - no warm-up line',
        'One idea per line with blank lines between thoughts',
        'Include at least one concrete figure (number, $ amount, or named role)',
        'If there is any ask or link, it must be the final line',
        'No corporate buzzwords (leverage, synergy, game-changer, seamless, unlock)',
      ],
      scorePlatform: 'linkedin',
    },
    {
      id: 'x-one-liner',
      label: 'X one-liner',
      platform: 'X (Twitter)',
      description: 'A single quotable declarative line.',
      constraints: [
        'Exactly one sentence, under 200 characters',
        'Quotable and declarative - no question, no link, no hashtags',
        'Include one concrete number or named role if the topic allows',
        'First-person voice, written like you would say it out loud',
      ],
      scorePlatform: 'x',
    },
    {
      id: 'x-thread-opener',
      label: 'X thread opener',
      platform: 'X (Twitter)',
      description: 'Tweet 1 of a thread - hook plus a reason to expand.',
      constraints: [
        'Write 40-120 words as the first tweet of a thread',
        'Open on a number or a scene that creates a curiosity gap',
        'Promise a specific payoff for reading the thread',
        'No links and no hashtags in the opener',
        'Short lines with whitespace - one idea per line',
      ],
      scorePlatform: 'x',
    },
    {
      id: 'short-email',
      label: 'Short email',
      platform: 'Email',
      description: 'Subject + under-120-word body with one CTA.',
      constraints: [
        'Start with a "Subject:" line, then a blank line, then the body',
        'Body under 120 words with exactly one call to action',
        'Reference one concrete number or outcome',
        'The call to action must be the final line before the sign-off',
        'Plain conversational voice - no corporate buzzwords',
      ],
      scorePlatform: 'x',
    },
  ];
}

/**
 * Build the exact worker prompt for one topic x format pair. Deterministic:
 * same inputs, same string. Embeds the format label, platform, and every
 * constraint so the model gets the full spec, and ends with an
 * output-discipline line ("only") so replies are paste-ready content, not
 * meta commentary.
 */
export function buildGenerationPrompt(topic: string, format: ContentFormatSpec): string {
  const cleanTopic = topic.trim();
  return [
    `You are a sales content writer. Write a ${format.label} for ${format.platform}.`,
    '',
    `Topic / brief: ${cleanTopic}`,
    '',
    'Platform constraints (follow every one):',
    ...format.constraints.map((c) => `- ${c}`),
    '',
    'Respond with only the finished content - no preamble, no explanation, no quotes around it.',
  ].join('\n');
}
